// Greenhold <TierBadge> — the canonical tier-badge primitive (Story 3.5).
//
// Pill-shaped badge rendering the tier identity (Common / Preferred /
// Premium / Founder's) — tier fill background + WCAG-paired foreground
// text + tier name in monospace uppercase + optional tier-shape SVG
// icon prefix for color-blind safety beyond the text-only guarantee
// (UX-DR13 + UX-DR47 + NFR-A11y-7).
//
// Story 3.5 — the THIRD bespoke greenhold/* component in the Epic-3
// trio (after Story 3.3 Certificate + Story 3.4 SealedDossier state-
// machine extension):
//   * Wraps shadcn <Badge variant="outline" /> from src/ui/badge.tsx
//     (Story 1.3) so future shadcn Badge upgrades (focus rings, etc.)
//     are inherited automatically. tailwind-merge resolves the
//     conflicting classes so our tier bg + fg + custom padding/radius
//     win cleanly.
//   * 4 tier visual variants via the TIER_VARIANTS Record pattern
//     (mirrors Story 3.3 Certificate's TIER_VARIANTS but trimmed —
//     TierBadge is intentionally tier-uniform; NO Founder's-specific
//     shimmer / lifted shadow / glow on the badge itself per UX-DR13).
//   * 3 size variants (sm/md/lg per epic AC L1380) via the
//     SIZE_CLASSES Record pattern (mirrors Stories 2.7 / 3.3 / 3.4).
//   * Optional withIcon prop renders one of 4 inline SVG primitives
//     (square / diamond / hexagon / star) mapped to tiers by rarity
//     escalation. Icons are 1em-scaled + fill="currentColor" so they
//     stay paired with the text-on-background contrast guarantee from
//     Story 1.2's WCAG-verified pairs.
//
// Story 3.5 EXECUTES the swap closure on Story 3.3 Certificate's
// "ship usable + extend later" decision: Certificate.tsx's inline
// tier-badge fallback at L521-L535 + the dead `badgeBgClass /
// badgeFgClass / badgeLabel` fields in its TIER_VARIANTS Record + the
// private `tierBadgeLabel` helper all get REPLACED in the same
// Story 3.5 commit. Certificate's public API stays UNCHANGED (verified
// by typecheck + `/prototype/certificate` post-swap regression).
//
// Story 3.5 ALSO swaps the Story 1.3 App.tsx homepage smoke-test
// tier-pill row (4 hardcoded <span> elements) for a <TierBadge> loop —
// the last inline-tier-badge in the codebase is closed.
//
// Future consumers (Story 4.x vault grid + share-detail page; Story
// 5.x marketplace listing card; Story 3.6 reveal-ceremony tier-beat
// overlay if it chooses to use a static badge) all consume this one
// shared component instead of re-implementing the same pill.

import type { FC } from "react";
import { Badge } from "../ui/badge";
import { cn } from "../lib/utils";

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

/** Tier identity — mirrors server::ShareTier + Story 3.3 CertificateTier
 *  + the implicit tier slot Story 3.4 SealedDossierData hides until
 *  reveal. Single source of truth for SPA tier vocabulary. */
export type TierBadgeTier = "common" | "preferred" | "premium" | "founders";

/** Visual size variant.
 *  - `sm` (default, 11px) — vault grid + marketplace listings +
 *    Certificate thumb/md.
 *  - `md` (14px) — Certificate lg.
 *  - `lg` (18px) — Certificate hero + Story 3.6 opening-ceremony
 *    tier-beat overlay if/when it consumes a static badge. */
export type TierBadgeSize = "sm" | "md" | "lg";

export interface TierBadgeProps {
  tier: TierBadgeTier;
  size?: TierBadgeSize;
  /** When true, prefixes a small tier-shape SVG icon (square /
   *  diamond / hexagon / star — mapped by rarity escalation) inside
   *  the pill. Color-blind affordance beyond the text-only safety
   *  net; `aria-hidden="true"` on the icon since the tier text
   *  already carries the screen-reader meaning. */
  withIcon?: boolean;
  /** Optional className for outer container — supports composition.
   *  Resolved via tailwind-merge inside cn(); consumer overrides win. */
  className?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tier variants — 4 tiers × bgClass + fgClass + label + iconShape.
// The icon-shape mapping is hard-coded by intuitive rarity escalation
// (common = simplest geometric primitive → founders = star apex). UX
// spec doesn't lock the mapping; if a future polish wants to swap
// shapes, the public API stays `withIcon: boolean` either way.
// ─────────────────────────────────────────────────────────────────────────────

type IconShape = "square" | "diamond" | "hexagon" | "star";

const TIER_VARIANTS: Record<TierBadgeTier, {
  bgClass: string;
  fgClass: string;
  label: string;
  iconShape: IconShape;
}> = {
  common:    { bgClass: "bg-tier-common-fill",    fgClass: "text-tier-common-fg",    label: "COMMON",    iconShape: "square"  },
  preferred: { bgClass: "bg-tier-preferred-fill", fgClass: "text-tier-preferred-fg", label: "PREFERRED", iconShape: "diamond" },
  premium:   { bgClass: "bg-tier-premium-fill",   fgClass: "text-tier-premium-fg",   label: "PREMIUM",   iconShape: "hexagon" },
  founders:  { bgClass: "bg-tier-founders-fill",  fgClass: "text-tier-founders-fg",  label: "FOUNDER'S", iconShape: "star"    },
};

// ─────────────────────────────────────────────────────────────────────────────
// Size variants — 3 sizes per epic AC L1380.
// Padding scales with font-size; the `inline-flex items-center gap-1.5`
// layout (from the base) handles icon + text spacing uniformly.
// ─────────────────────────────────────────────────────────────────────────────

const SIZE_CLASSES: Record<TierBadgeSize, {
  padding: string;
  fontSize: string;
}> = {
  // text-[11px] is an arbitrary value because Tailwind's text-xs is 12px,
  // not the 11px the epic AC specifies for the `sm` baseline.
  sm: { padding: "px-2.5 py-1",  fontSize: "text-[11px]" },
  md: { padding: "px-3 py-1",    fontSize: "text-sm"     },
  lg: { padding: "px-4 py-1.5",  fontSize: "text-lg"     },
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers (pure formatting — no side effects).
// ─────────────────────────────────────────────────────────────────────────────

/** Returns the per-tier display label. The Founder's apostrophe is the
 *  U+0027 APOSTROPHE (not a smart quote) so it matches the canonical
 *  ShareDto/CertificateShare data + grep-able in tests.
 *
 *  Story 3.3 Certificate's aria-label imports + calls this helper after
 *  Story 3.5 swap closure — single source of truth across the codebase. */
export function tierBadgeLabel(tier: TierBadgeTier): string {
  return TIER_VARIANTS[tier].label;
}

// ─────────────────────────────────────────────────────────────────────────────
// TierIcon — internal 1em-scaled SVG primitive (4 shapes).
// fill="currentColor" so the icon inherits the badge's text-tier-*-fg
// and preserves the WCAG contrast pairing. aria-hidden because the
// text label is the accessible name per UX-DR13.
// ─────────────────────────────────────────────────────────────────────────────

const TierIcon: FC<{ shape: IconShape }> = ({ shape }) => {
  const commonProps = {
    width: "1em",
    height: "1em",
    viewBox: "0 0 12 12",
    fill: "currentColor",
    "aria-hidden": true as const,
    // shrink-0 so the icon doesn't get squashed by flex layout when the
    // text wraps (although whitespace-nowrap on the badge prevents wrap;
    // belt-and-braces).
    className: "shrink-0",
  };
  switch (shape) {
    case "square":
      return (
        <svg {...commonProps}>
          <rect x="1" y="1" width="10" height="10" />
        </svg>
      );
    case "diamond":
      return (
        <svg {...commonProps}>
          <polygon points="6,1 11,6 6,11 1,6" />
        </svg>
      );
    case "hexagon":
      return (
        <svg {...commonProps}>
          <polygon points="3,1 9,1 11.5,6 9,11 3,11 0.5,6" />
        </svg>
      );
    case "star":
      return (
        <svg {...commonProps}>
          <polygon points="6,0.5 7.4,4.2 11.4,4.5 8.2,6.9 9.4,10.7 6,8.3 2.6,10.7 3.8,6.9 0.6,4.5 4.6,4.2" />
        </svg>
      );
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pill-shaped tier badge. Renders the tier name in monospace uppercase
 * with the tier's fill color background + WCAG-paired foreground text.
 * Optionally prefixes a tier-shape icon for color-blind safety.
 *
 * Wraps shadcn <Badge variant="outline" />; tailwind-merge inside cn()
 * resolves the conflicting classes so our tier bg + fg + custom
 * padding/radius/font win over the shadcn base.
 */
export const TierBadge: FC<TierBadgeProps> = ({
  tier,
  size = "sm",
  withIcon = false,
  className,
}) => {
  const variant = TIER_VARIANTS[tier];
  const sizing = SIZE_CLASSES[size];

  return (
    <Badge
      variant="outline"
      className={cn(
        // Layout — gap-1.5 (6px) spacing between icon and text. The
        // shadcn base already provides `inline-flex items-center`; we
        // add gap + the rest of the visual overrides on top.
        "gap-1.5 whitespace-nowrap",
        // Shape — pill via rounded-sm (2px; epic AC asks for 3px but
        // Tailwind has no native 3px utility; 1-pixel deviation below
        // visual-perception threshold).
        "rounded-sm border-0",
        // Typography — monospace, uppercase, 0.08em letter-spacing
        // (the `tracking-wider` token from packages/tokens/src/index.ts).
        "font-mono uppercase tracking-wider font-medium",
        // Tier colors — bg fill + WCAG-paired fg.
        variant.bgClass,
        variant.fgClass,
        // Size — per-variant padding + font-size.
        sizing.padding,
        sizing.fontSize,
        // Consumer overrides (last so tailwind-merge gives them
        // priority).
        className,
      )}
    >
      {withIcon && <TierIcon shape={variant.iconShape} />}
      {variant.label}
    </Badge>
  );
};

export default TierBadge;
